import { urls } from '~/preloaded';
import { TYPE_GLOBAL_SEARCH } from '~/constants';
import { get } from '~/helpers/api';
import Loader from '~/helpers/loader';
import { t } from '~/helpers/localization';
import { getMajorLeagueNumber, getSeasonInfo, getSeasonLeagues, isLeaguePresentInSeason } from '~/helpers/seasons';
import { fetchAutompleteResultThunk } from '~/store/thunks/autocompleteThunks';
import { fetchSearchResultThunk } from '~/store/thunks/searchThunks';

import type { WowsLadderType } from '~/Actions/ActionClanProfile';
import type { InferActionsType } from '~/Reducers';
import type { SEASON_TYPES } from '~/constants';
import type { AppThunk, AppAsyncThunk } from '~/store';
import type { IApiClanBattlesRatingResponse } from '~/types/api';
import type { IClanRatingData } from '~/types/declaration';

export const TOGGLE_LEAGUE_AND_DIVISION = 'TOGGLE_LEAGUE_AND_DIVISION';
export const TOGGLE_RATING_LIST = 'TOGGLE_RATING_LIST';
export const TOGGLE_RATING_ERROR = 'TOGGLE_RATING_ERROR';
export const TOGGLE_RATING_FETCH = 'TOGGLE_RATING_FETCH';
export const SET_CROSS_REALM_LADDER = 'SET_CROSS_REALM_LADDER';
export const SET_CURRENT_CLAN_ID = 'SET_CURRENT_CLAN_ID';
export const SET_CURRENT_SEASON_TYPE = 'SET_CURRENT_SEASON_TYPE';
export const SET_CURRENT_SEASON = 'SET_CURRENT_SEASON';
export const SET_CURRENT_REALM = 'SET_CURRENT_REALM';

export type ActionsType = InferActionsType<typeof actionsClanRating>;
type LeagueAndDivisionType = {
  league: number;
  division: number;
  error?: string;
  errors?: unknown;
};

export type ToggleRatingListType = {
  ratingList?: LeagueAndDivisionType[];
  league: number;
  division: number;
  [league: number]: {
    [divisions: number]: IClanRatingData[];
  };
};

export type LeagueDivisionRatingType = {
  clanId?: number | null;
  division?: number;
  league?: number;
  ownClan?: boolean;
  realm?: string;
  withGlobalSpinner?: boolean;
};

export const actionsClanRating = {
  setCrossRealmLadder: (isCrossRealmLadder: boolean) =>
    ({
      type: SET_CROSS_REALM_LADDER,
      isCrossRealmLadder,
    }) as const,

  toggleRatingFetch: (isFetching: boolean) =>
    ({
      type: TOGGLE_RATING_FETCH,
      isFetching,
    }) as const,

  setCurrentSeason: (currentSeason: number) =>
    ({
      type: SET_CURRENT_SEASON,
      currentSeason,
    }) as const,

  setCurrentRealm: (currentRealm: string) =>
    ({
      type: SET_CURRENT_REALM,
      currentRealm,
    }) as const,

  setCurrentClanId: (currentClanId: number) =>
    ({
      type: SET_CURRENT_CLAN_ID,
      currentClanId,
    }) as const,

  setLeagueAndDivision: ({ league, division }: LeagueAndDivisionType) =>
    ({
      type: TOGGLE_LEAGUE_AND_DIVISION,
      league,
      division,
    }) as const,

  toggleRatingError: (isError: boolean, errorMessage: string) =>
    ({
      type: TOGGLE_RATING_ERROR,
      isError,
      errorMessage,
    }) as const,

  setCurrentSeasonType: (currentSeasonType: SEASON_TYPES) =>
    ({
      type: SET_CURRENT_SEASON_TYPE,
      currentSeasonType,
    }) as const,

  toggleRatingList: ({
    ratingList,
    league,
    division,
  }: {
    ratingList?: IClanRatingData[];
    league: ToggleRatingListType['league'];
    division: ToggleRatingListType['division'];
  }) =>
    ({
      type: TOGGLE_RATING_LIST,
      ratingList,
      league,
      division,
    }) as const,
};

let lastFetchUrl = '';
export const getLeagueDivisionRating =
  (
    { league, division, clanId, realm }: LeagueDivisionRatingType,
    withGlobalSpinner = false,
    ownClan = true,
  ): AppAsyncThunk =>
  async (dispatch, getState) => {
    const url = urls.ladderStructure;
    let currentUrl = '';
    let id: number | null = null;

    const state = getState();
    const currentSeason = state.clansRating.currentSeason;
    const season = getSeasonInfo(currentSeason);
    const firstLeague = getMajorLeagueNumber(season);

    if (typeof league !== 'undefined') {
      if (!isLeaguePresentInSeason(season, league)) {
        league = undefined;
      }
    }

    if (ownClan) {
      const isQualified = clanId && state.clans.wowsLadder[clanId]?.is_qualified;

      id = state.currentAccount.clanId;

      if (clanId && isQualified) {
        currentUrl = `${url}?clan_id=${clanId}`;
      } else if (league === undefined || !division) {
        currentUrl = url;
      } else {
        currentUrl = `${url}?league=${league}&division=${division}`;
      }
    } else if (clanId) {
      id = clanId;
      currentUrl = `${url}?clan_id=${clanId}`;
    } else {
      currentUrl = url;
    }

    realm = realm || state.clansRating.currentRealm || TYPE_GLOBAL_SEARCH;

    const sep = url === currentUrl ? '?' : '&';
    currentUrl = `${currentUrl}${sep}season=${currentSeason}`;
    currentUrl += `&realm=${realm}`;

    dispatch(actionsClanRating.setCurrentClanId(id ?? 0));

    if (currentUrl === lastFetchUrl) {
      return;
    } else {
      lastFetchUrl = currentUrl;
    }

    const loader = withGlobalSpinner ? new Loader() : null;
    loader?.start(dispatch);

    try {
      const response = await get<IApiClanBattlesRatingResponse>(currentUrl);

      if (response.errors || response.error) {
        lastFetchUrl = '';
        dispatch(actionsClanRating.setLeagueAndDivision({ league: firstLeague, division: 1 }));
        dispatch(actionsClanRating.toggleRatingList({ league: firstLeague, division: 1 }));
        dispatch(actionsClanRating.toggleRatingError(true, t('Произошла ошибка. Повторите попытку позже')));
      } else {
        let selectedLeague: number | undefined;
        let selectedDivision: number | undefined;

        if (response.length) {
          selectedLeague = response[0].league;
          selectedDivision = response[0].division;
        } else {
          selectedLeague = league || firstLeague;
          selectedDivision = division || 1;
        }

        dispatch(actionsClanRating.toggleRatingFetch(false));
        dispatch(actionsClanRating.setLeagueAndDivision({ league: selectedLeague, division: selectedDivision }));
        dispatch(actionsClanRating.toggleRatingError(false, ''));
        dispatch(
          actionsClanRating.toggleRatingList({
            ratingList: response,
            league: selectedLeague,
            division: selectedDivision,
          }),
        );
      }
    } catch (error) {
      lastFetchUrl = '';
      dispatch(actionsClanRating.setLeagueAndDivision({ league: firstLeague, division: 1 }));
      dispatch(
        actionsClanRating.toggleRatingList({
          league: league ?? firstLeague,
          division: division ?? 1,
        }),
      );
      dispatch(actionsClanRating.toggleRatingFetch(false));
      dispatch(actionsClanRating.toggleRatingError(true, t('Произошла ошибка. Повторите попытку позже')));
    } finally {
      loader?.stop(dispatch);
    }
  };

export const toggleCurrentSeasonType =
  (seasonType: SEASON_TYPES): AppThunk =>
  (dispatch, getState) => {
    const state = getState();
    const clanId = state.currentAccount.clanId;

    dispatch(actionsClanRating.setCurrentSeasonType(seasonType));
    void dispatch(getLeagueDivisionRating({ clanId }));
  };

export const toggleCurrentRealm =
  (realm: string): AppThunk =>
  (dispatch, getState) => {
    const state = getState();
    const league = state.clansRating.currentLeague;
    const division = state.clansRating.currentDivision;

    dispatch(actionsClanRating.setCurrentRealm(realm));
    void dispatch(getLeagueDivisionRating({ league, division, realm }));
  };

export const toggleCurrentSeason =
  (currentSeason: number): AppThunk =>
  (dispatch, getState) => {
    const state = getState();
    const league = state.clansRating.currentLeague;
    const division = state.clansRating.currentDivision;

    dispatch(actionsClanRating.setCurrentSeason(currentSeason));
    void dispatch(getLeagueDivisionRating({ league, division }));
  };

export const toggleCrossRealmLadder =
  (isCrossRealmLadder: boolean): AppThunk =>
  (dispatch, getState) => {
    const state = getState();
    const league = state.clansRating.currentLeague;
    const division = state.clansRating.currentDivision;
    const term = state.searchResult.term;
    const autocompleteTerm = state.autocomplete.foundByTerm;

    dispatch(actionsClanRating.setCrossRealmLadder(isCrossRealmLadder));

    if (term !== '') {
      void dispatch(fetchSearchResultThunk({ type: 'clansRating' }));
    }

    void dispatch(fetchAutompleteResultThunk({ term: autocompleteTerm, type: 'clansRating' }));
    void dispatch(getLeagueDivisionRating({ league, division }));
  };

export const toggleLeague =
  (league: number, ladderIsUpdate: boolean): AppThunk =>
  (dispatch, getState) => {
    const state = getState();

    if (state.clansRating.currentLeague === league && !ladderIsUpdate && !state.clansRating.isError) {
      return;
    }

    const seasonLeagues = getSeasonLeagues(state.clansRating.currentSeason);
    const firstDivision = seasonLeagues[league].divisions.length - 1;
    let division = state.clansRating.currentDivision;
    if (!seasonLeagues[league].divisions.find((div) => div.number === division)) {
      division = seasonLeagues[league].divisions[firstDivision].number;
    }

    void dispatch(getLeagueDivisionRating({ league, division }));
  };

export const toggleDivision =
  (division: number, ladderIsUpdate: boolean): AppThunk =>
  (dispatch, getState) => {
    const state = getState();
    const { currentDivision, currentSeason, currentLeague, isError } = state.clansRating;

    if (currentDivision === division && !ladderIsUpdate && !isError) {
      return;
    }

    const currentAccountClanId = state.currentAccount.clanId;
    const currentWowsLadder = currentAccountClanId ? state.clans.wowsLadder[currentAccountClanId] : null;

    let league = currentLeague;
    let ladderData: WowsLadderType | null = null;
    let clanId: number | null = null;

    const season = getSeasonInfo(currentSeason);
    const firstLeague = getMajorLeagueNumber(season);

    if (league === undefined) {
      league = season.leagues[firstLeague].number;
    }

    if (currentWowsLadder) {
      ladderData = currentWowsLadder;

      if (ladderData.division === division && ladderData.league === league) {
        clanId = currentAccountClanId;
      }
    }

    void dispatch(getLeagueDivisionRating({ league, division, clanId }));
  };
